import { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { productReviewsService } from '@services/businessContainer';
import { IProduct, IProductReviewed } from '@monofront/types';
import { useQuery, useLazyQuery } from '@apollo/client';
import {
  GET_CUSTOMER_ORDER,
  GET_PRODUCT_REVIEWS_BY_CUSTOMER,
} from '@monofront/business/queries';
import HeartLoader from '@monofront/design-system/components/HeartLoader';
import ReadMore from '@monofront/design-system/components/ReadMore';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import EmptyProducts from '@components/EmptyProducts';
import ReviewStarsSelect from '@components/ReviewStarsSelect';
import ReviewProductModal from './ReviewProductModal';
import ReviewStars from '@components/ReviewStars';
import WebviewLink from '@components/WebviewLink';
import { useTranslations } from 'use-intl';
import { isMobileApp } from '@utils/environment';
import useI18n from '@hooks/i18n/useI18n';
import { useFormatProductWeight } from '@monofront/business/hooks/product/useFormatProductWeight';

const OrderedProductsReview: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { formatProductWeight } = useFormatProductWeight();
  const tReviews = useTranslations('Reviews');
  const tAccount = useTranslations('Account');
  const tErrors = useTranslations('Errors');
  const tCatalog = useTranslations('Catalog');
  const tGlobal = useTranslations('Global');
  const tCart = useTranslations('Cart');

  const { channel } = useI18n();

  const [productToReviewSku, setProductToReviewSku] = useState<string | null>(
    null,
  );
  const [productToReview, setProductToReview] = useState<IProduct | null>(null);
  const [initialRatingSelected, setInitialRatingSelected] = useState<
    null | number
  >(null);
  const [productsReviewed, setProductsReviewed] = useState<IProductReviewed[]>(
    [],
  );
  const [sortedProducts, setSortedProducts] = useState<IProduct[] | null>(null);

  const {
    data: getCustomerOrderQueryResult,
    loading: isGetCustomerOrderLoading,
    error: getCustomerOrderError,
  } = useQuery(GET_CUSTOMER_ORDER, {
    variables: {
      cursor: '',
      pageSize: 1,
    },
    ssr: false,
    fetchPolicy: 'cache-and-network',
  });

  const [
    getProductReviewsByCustomerQuery,
    {
      loading: isGetProductReviewsByCustomerLoading,
      data: productReviewsByCustomer,
      error: getProductReviewByCustomerError,
    },
  ] = useLazyQuery(GET_PRODUCT_REVIEWS_BY_CUSTOMER, {
    ssr: false,
    fetchPolicy: 'cache-and-network',
  });

  const orderProductsSku = useMemo(() => {
    const items = getCustomerOrderQueryResult?.getCustomerOrder?.items ?? [];

    if (!items.length) {
      return [];
    }

    return items[0]?.lineItems?.map(({ sku }) => sku).filter(Boolean) ?? [];
  }, [getCustomerOrderQueryResult, getCustomerOrderError]);

  useEffect(() => {
    if (getCustomerOrderError) {
      toast(tErrors('fetchProductsError'), {
        progressClassName: 'toast-error',
      });
    }
  }, [getCustomerOrderError]);

  // Fetch product reviews for the SKUs when they are available
  useEffect(() => {
    if (!orderProductsSku.length) {
      return;
    }

    getProductReviewsByCustomerQuery({
      variables: { skus: orderProductsSku },
    });
  }, [orderProductsSku, getProductReviewsByCustomerQuery]);

  // When reviews data changes, recompute products to review / reviewed
  useEffect(() => {
    if (!productReviewsByCustomer?.getProductReviewsByCustomer) {
      return;
    }

    const getAndSortReviews = async () => {
      try {
        const { productsToReview, productsReviewed } =
          await productReviewsService.getProductsSorted(
            orderProductsSku,
            productReviewsByCustomer.getProductReviewsByCustomer,
            channel,
          );

        setSortedProducts(productsToReview);
        setProductsReviewed(productsReviewed);
      } catch (error) {
        toast(tErrors('fetchProductsError'), {
          progressClassName: 'toast-error',
        });
      }
    };

    void getAndSortReviews();
  }, [
    productReviewsByCustomer?.getProductReviewsByCustomer,
    orderProductsSku,
    channel,
  ]);

  useEffect(() => {
    if (getProductReviewByCustomerError) {
      toast(tErrors('fetchProductsError'), {
        progressClassName: 'toast-error',
      });
    }
  }, [getProductReviewByCustomerError]);

  if (isGetCustomerOrderLoading || isGetProductReviewsByCustomerLoading) {
    return <HeartLoader />;
  }

  return (
    <section className="limited-width-container products-review">
      <ReviewProductModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        initialRatingSelected={initialRatingSelected}
        productSku={productToReviewSku as string}
        product={productToReview}
        onSuccess={() => {
          if (!orderProductsSku.length) {
            return;
          }

          getProductReviewsByCustomerQuery({
            variables: { skus: orderProductsSku },
            fetchPolicy: 'network-only',
          });
        }}
      />
      <h1 className="desktop-only">{tAccount('myProductsToReview')}</h1>
      {!getCustomerOrderQueryResult?.getCustomerOrder?.items?.length ? (
        <EmptyProducts
          ctaLabel={tCatalog('seeTheSelection')}
          text={tCart('emptyOrderList')}
        />
      ) : (
        <>
          <h2>{tReviews('productsOfMyLastOrder')}</h2>
          <div className="product-list">
            {sortedProducts?.map((product) => (
              <div key={product.sku} className="product-block">
                <div className="product-title">
                  <WebviewLink
                    href={`/products/${product.handle}`}
                    mobileMessage={{
                      type: 'goToProduct',
                      parameters: { handle: product.handle },
                    }}
                  >
                    <>
                      {product.title}{' '}
                      {formatProductWeight(product.meta?.finance)}
                    </>
                  </WebviewLink>
                </div>
                <div className="product-details">
                  <div className="product-image">
                    <WebviewLink
                      href={`/products/${product.handle}`}
                      mobileMessage={{
                        type: 'goToProduct',
                        parameters: { handle: product.handle },
                      }}
                    >
                      <ResponsiveImage
                        src={product.image!}
                        alt={product.title}
                        width={120}
                        height={120}
                      />
                    </WebviewLink>
                  </div>
                  <div className="product-infos">
                    <p>{tReviews('rateThisProduct')}</p>
                    <div>
                      <ReviewStarsSelect
                        onClick={() => {
                          setProductToReviewSku(product.sku);
                          setProductToReview(product);
                          setIsModalOpen(true);
                        }}
                        ratingSelected={null}
                        setRatingSelected={setInitialRatingSelected}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {productsReviewed.length ? (
        <div>
          <div className="separator" />
          <h2>{tReviews('myReviewedProducts')}</h2>
          <div className="product-list">
            {productsReviewed?.map((review) => (
              <div key={review.reviewSubject.code} className="product-block">
                <div className="product-title">
                  <WebviewLink
                    href={`/products/${review.handle}`}
                    mobileMessage={{
                      type: 'goToProduct',
                      parameters: { handle: review.handle },
                    }}
                  >
                    {`${review.productTitle} ${formatProductWeight(
                      review.finance,
                    )}`}
                  </WebviewLink>
                </div>
                <div className="product-details">
                  <div className="product-image">
                    <WebviewLink
                      href={`/products/${review.handle}`}
                      mobileMessage={{
                        type: 'goToProduct',
                        parameters: { handle: review.handle },
                      }}
                    >
                      <ResponsiveImage
                        src={review.image!}
                        alt={review.title}
                        width={120}
                        height={120}
                      />
                    </WebviewLink>
                  </div>
                  <div className="product-infos">
                    <p>{tReviews('yourReview')}</p>
                    <div>
                      <ReviewStars averageRating={review.rating} />
                    </div>
                    {review.comment.length > 100 ? (
                      <ReadMore
                        backgroundColor="var(--readMore)"
                        clampLimit={4}
                        isCondensed
                        seeMoreText={tGlobal('seeMore')}
                        seeLessText={tGlobal('seeLess')}
                      >
                        <p className="comment">{review.comment}</p>
                      </ReadMore>
                    ) : (
                      <p className="comment">{review.comment}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      <style jsx>{`
        section {
          margin-top: ${isMobileApp ? 0 : 'var(--space-5)'};
          margin-bottom: var(--space-5);
        }

        h1,
        h2 {
          margin-bottom: var(--space-5);
        }

        h2 {
          font-weight: var(--font-weight-bold);
          font-size: var(--font-size-3);
          font-family: var(--font-family-1);
        }

        .product-list {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          grid-gap: var(--space-5);
        }

        .product-title {
          background-color: var(--clr-primary-5);
          border-radius: var(--rd-2);
          padding: var(--space-5);
          width: 100%;
          font-weight: var(--font-weight-bold);
        }

        .product-details {
          padding: var(--space-5);
          display: flex;
          flex-direction: row;
        }

        .product-infos {
          display: flex;
          flex-direction: column;
          justify-content: center;
          width: 80%;
        }

        .product-image {
          margin-right: var(--space-4);
        }

        .comment {
          margin-top: var(--space-4);
        }

        .separator {
          border: var(--border);
          margin: var(--space-8) 0;
        }

        @media (max-width: 800px) {
          .product-list {
            grid-template-columns: repeat(1, 1fr);
          }
          section {
            padding: ${isMobileApp
              ? 'var(--space-5)'
              : 'var(--padding-layout-sm)'};
          }
        }
      `}</style>
    </section>
  );
};

export default OrderedProductsReview;
